import { API_BASE, ApiError } from '@/api/http';
import { emitSessionSignal, revocationReasonOf } from '@/api/sessionSignals';
import type { StreamEvent, StreamEventKind, StreamTicket } from '@/api/types';
import { isStreamEventKind, parseStreamEvent, STREAM_EVENT_NAMES } from './events';

/**
 * The `EventSource` client for `GET /api/v1/events` (ADR 0002, ADR 0041).
 *
 * **One stream per tab, for every account.** Commands stay REST: nothing is
 * ever sent *up* this connection, so there is no send path here at all.
 *
 * ## Why this no longer looks like a normal `EventSource` client
 *
 * `EventSource` has no header API, so the stream cannot carry
 * `X-Session-Proof` like every other request does. ADR 0041 settles that with
 * a **single-use ticket**: an authenticated, proof-bound `POST /events/ticket`
 * returns one, and the stream opens as `?ticket=<t>`.
 *
 * The ticket goes in a query string, which the shared edge logs in full
 * (`%{+Q}r`, doc 08 §8.4). That is survivable for exactly one reason: the
 * ticket is consumed by the open, milliseconds after it is written to the log,
 * and the server `GETDEL`s it. So the rule this module enforces without
 * exception is **never reuse a ticket and never store one** — every open,
 * including every reconnect, mints a fresh one.
 *
 * The cost is `EventSource`'s automatic reconnect, which reuses the URL and
 * would therefore present a spent ticket. It is given up deliberately: any
 * error, in any `readyState`, closes the source at once (the server's
 * `retry: 3000` leaves a comfortable window before the browser would try on
 * its own) and we reopen by hand with a new ticket and `?after=<last id>`.
 * That is the resume ADR 0002 already specified and `events.go` already
 * honours, so nothing is lost that was not already lost — `Last-Event-ID`
 * simply will not be seen, because the URL is new every time.
 *
 * The mint also **replaces the status probe**. `EventSource` cannot see a
 * status code; the mint can, and it answers the same question with a real one:
 * `401` session gone, `403` CSRF/origin, `429` the session's 8-stream cap
 * (checked at mint as well as at open), `503` datastore. The existing backoff
 * table applies unchanged.
 */

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'stopped';

export interface StreamConnectionOptions {
  /**
   * Mints a fresh, single-use stream ticket. Called once per open attempt,
   * never cached by this module.
   */
  mintTicket: (signal?: AbortSignal) => Promise<StreamTicket>;
  /** Called once per named frame, already parsed. */
  onEvent: (event: StreamEvent) => void;
  onStatus?: (status: StreamStatus, detail?: string) => void;
  /** Injectable for tests; defaults to the global. */
  eventSourceFactory?: (url: string) => EventSource;
  /** Injectable for tests. */
  setTimeoutFn?: (fn: () => void, ms: number) => unknown;
  clearTimeoutFn?: (handle: unknown) => void;
  /** Injectable for tests; defaults to `Date.now`. */
  nowFn?: () => number;
}

export interface StreamConnection {
  start(): void;
  stop(): void;
  status(): StreamStatus;
  /** The last event id seen, which is what a reopen resumes from. */
  lastEventId(): string | undefined;
  /**
   * "You are back — prove you are still connected."
   *
   * Called on `visibilitychange`, `focus` and `online`. It is the answer to the
   * two ways a returning user found a dead stream:
   *
   * - **A pending backoff.** A tab that failed a few times is sitting on a
   *   timer of up to a minute, and while it was hidden the browser throttled
   *   that timer to roughly one tick a minute on top. `wake` drops the timer
   *   and reconnects now.
   * - **A half-open socket.** After a laptop sleeps, or a NAT rebinds, the TCP
   *   connection is gone but nothing tells the page: `readyState` still says
   *   OPEN and `onerror` never fires. Nothing arrives, forever. `wake` checks
   *   the heartbeat clock and reopens a stream that has gone quiet.
   */
  wake(): void;
  /** Whether a heartbeat has been seen recently enough to believe the socket. */
  healthy(): boolean;
}

/** `GET /api/v1/events` — the one account-less mail route besides `/accounts`. */
export function eventsUrl(base: string = API_BASE): string {
  return `${base.replace(/\/$/, '')}/events`;
}

/**
 * Builds the open URL.
 *
 * The ticket is the only credential this system ever places in a query string,
 * and `after` is an opaque event id, not a credential at all.
 */
export function streamUrl(
  ticket: string,
  after: string | undefined,
  base: string = API_BASE,
): string {
  const params = new URLSearchParams({ ticket });
  if (after !== undefined && after !== '') params.set('after', after);
  return `${eventsUrl(base)}?${params.toString()}`;
}

/** Backoff for an ordinary refusal: 3 s, doubling, capped at a minute. */
const BASE_DELAY_MS = 3_000;
const MAX_DELAY_MS = 60_000;
/**
 * Backoff after a `429`.
 *
 * The cap is per *session*, not per connection, so the other seven streams are
 * other tabs of the same person and they are not going away in three seconds.
 * A short retry here is a retry loop against a server that has already said no.
 */
const RATE_LIMITED_DELAY_MS = 60_000;

/**
 * The server's named liveness frame, and how long the client will wait for one.
 *
 * `events.go` writes `: hb` as an SSE *comment* every 25 s, which keeps
 * intermediaries from closing the stream — and is invisible to JavaScript,
 * because `EventSource` never surfaces comments. So the old client had no
 * liveness signal at all: a socket that died silently (laptop sleep, NAT
 * rebind, a proxy dropping a connection it no longer tracks) left
 * `readyState === OPEN`, no `error` event, and no mail, until the user
 * reloaded. That is the stale-list bug in its purest form.
 *
 * The server now also writes a **named** `heartbeat` frame on the same tick,
 * which JavaScript can see. Two missed beats plus slack is the limit; a stream
 * quieter than that is presumed dead and reopened.
 *
 * The watchdog is **armed only once a heartbeat has actually arrived**. Against
 * an older backend that sends only the comment, nothing arms, and the client
 * behaves exactly as it did before rather than reconnecting in a loop.
 */
export const HEARTBEAT_EVENT_NAME = 'heartbeat';
export const HEARTBEAT_TIMEOUT_MS = 70_000;

export function createStreamConnection(options: StreamConnectionOptions): StreamConnection {
  const makeSource =
    options.eventSourceFactory ??
    ((target: string) => new EventSource(target, { withCredentials: true }));
  const setTimer = options.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = options.clearTimeoutFn ?? ((handle) => clearTimeout(handle as number));

  const now = options.nowFn ?? (() => Date.now());

  let source: EventSource | undefined;
  let timer: unknown;
  let attempt = 0;
  let status: StreamStatus = 'idle';
  let stopped = false;
  /** The resume point. Survives every reconnect; that is its whole job. */
  let after: string | undefined;
  /** When the last frame of any kind arrived, and the watchdog watching for it. */
  let lastFrameAt = 0;
  let watchdog: unknown;
  /** Set by the first `heartbeat`; an older server never arms the watchdog. */
  let heartbeatsSeen = false;
  /**
   * Which open attempt is the current one.
   *
   * A mint is an `await`, and two things can ask for a reopen inside that
   * window — the `goodbye` frame the server sends before it closes, and the
   * `error` the closing socket then raises. Measured against the live build
   * that happened on every server-initiated cut: **two** EventSources were
   * opened five milliseconds apart and both stayed open, doubling the frames
   * the app applied and burning two of the session's eight-stream budget per
   * reconnect. Every attempt now takes a number and a late one stands down.
   */
  let generation = 0;

  const setStatus = (next: StreamStatus, detail?: string): void => {
    status = next;
    options.onStatus?.(next, detail);
  };

  const clearWatchdog = (): void => {
    if (watchdog !== undefined) clearTimer(watchdog);
    watchdog = undefined;
  };

  const closeSource = (): void => {
    clearWatchdog();
    if (source === undefined) return;
    source.close();
    source = undefined;
  };

  const stop = (detail?: string): void => {
    stopped = true;
    if (timer !== undefined) clearTimer(timer);
    timer = undefined;
    closeSource();
    setStatus('stopped', detail);
  };

  /** True once heartbeats are known to flow and one is overdue. */
  const silent = (): boolean =>
    heartbeatsSeen && lastFrameAt !== 0 && now() - lastFrameAt > HEARTBEAT_TIMEOUT_MS;

  /**
   * Re-arms the silence watchdog after every frame.
   *
   * A self-rescheduling timeout rather than an interval, so the injected timer
   * seam in the tests sees one pending handle at a time. While the tab is
   * hidden the browser throttles this to roughly a tick a minute, which only
   * delays the detection — `wake` is what makes a *returning* user prompt.
   */
  const armWatchdog = (): void => {
    clearWatchdog();
    if (stopped || !heartbeatsSeen) return;
    watchdog = setTimer(() => {
      watchdog = undefined;
      if (stopped || source === undefined) return;
      if (silent()) {
        // The socket is open as far as the browser is concerned and has not
        // delivered a heartbeat in three beats' worth of time. It is dead.
        attempt = 0;
        reopen();
        return;
      }
      armWatchdog();
    }, HEARTBEAT_TIMEOUT_MS);
  };

  const noteFrame = (): void => {
    lastFrameAt = now();
    armWatchdog();
  };

  const scheduleRetry = (delay: number): void => {
    if (stopped) return;
    setStatus('reconnecting');
    timer = setTimer(() => void connect(), delay);
  };

  /**
   * Turns a failed mint into the same decision the old probe made.
   *
   * The status is now authoritative rather than inferred, which is the one
   * thing the probe could never be sure of.
   */
  const handleMintFailure = (cause: unknown): void => {
    const status_ = cause instanceof ApiError ? cause.status : 0;
    const retryAfter = cause instanceof ApiError ? cause.retryAfter : undefined;
    switch (status_) {
      case 401:
        // A `proof_*` 401 is NOT this: the transport publishes its own `proof`
        // signal for those and the session layer shows the recovery dialog. By
        // the time a plain 401 reaches here the session really is gone.
        if (cause instanceof ApiError && cause.isProofFailure) {
          stop('device key');
          return;
        }
        // Revoked from elsewhere (a password change, or "Sign out other
        // sessions"): a sign-out with a reason, not a re-auth prompt. The
        // transport publishes the same signal for this request; the session
        // layer latches on the first, so saying it twice costs nothing and
        // keeps this path right for a transport that did not.
        if (cause instanceof ApiError && cause.isSessionRevoked) {
          emitSessionSignal({ type: 'revoked', reason: revocationReasonOf(cause.details) });
          stop('signed out');
          return;
        }
        emitSessionSignal({ type: 'expired', code: 'session_expired', status: 401 });
        stop('signed out');
        return;
      case 403:
        // Cross-site or CSRF refusal. Retrying reproduces it exactly.
        stop('refused');
        return;
      case 501:
        // This build has no event stream. Nothing to wait for.
        stop('not implemented');
        return;
      case 429: {
        attempt = 0;
        scheduleRetry(Math.max((retryAfter ?? 0) * 1_000, RATE_LIMITED_DELAY_MS));
        return;
      }
      default: {
        attempt += 1;
        scheduleRetry(Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS));
      }
    }
  };

  /**
   * Any stream failure: close, mint again, reopen from `after`.
   *
   * A stream that *was* open reopens immediately (`attempt` is reset by
   * `onopen`), which is the ADR 0041 behaviour — the cut is the ordinary edge
   * inactivity timeout and the user should not wait three seconds for their
   * mail to go live again. Repeated failures back off normally.
   */
  const reopen = (): void => {
    if (stopped) return;
    closeSource();
    if (attempt === 0) {
      attempt = 1;
      setStatus('reconnecting');
      void connect();
      return;
    }
    attempt += 1;
    scheduleRetry(Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS));
  };

  async function connect(): Promise<void> {
    if (stopped) return;
    generation += 1;
    const mine = generation;
    closeSource();
    setStatus('connecting');

    let ticket: StreamTicket;
    try {
      ticket = await options.mintTicket();
    } catch (cause) {
      if (stopped || mine !== generation) return;
      handleMintFailure(cause);
      return;
    }
    // A newer attempt started while this one was minting. Its ticket is
    // single-use and already spent by the newer open, so there is nothing to
    // release and nothing to do but stand down.
    if (stopped || mine !== generation) return;

    // The ticket is used here and never held: no field, no closure, no log.
    const es = makeSource(streamUrl(ticket.ticket, after));
    source = es;

    /*
     * Everything below answers only for the source it was attached to. A
     * source superseded by a newer attempt is closed but its already-queued
     * handlers still run, and letting one of those reopen the stream is how
     * two connections were ending up open at once.
     */
    const current = (): boolean => !stopped && source === es;

    es.onopen = () => {
      if (!current()) return;
      attempt = 0;
      noteFrame();
      setStatus('open');
    };

    /*
     * The named liveness frame (`events.go`). It is not part of the event
     * vocabulary — it carries no state and nothing reduces it — so it is
     * subscribed here rather than added to `STREAM_EVENT_NAMES`, where it
     * would have to become a `StreamEventKind` that no reducer handles.
     */
    es.addEventListener(HEARTBEAT_EVENT_NAME, () => {
      heartbeatsSeen = true;
      if (!current()) return;
      noteFrame();
    });

    es.onerror = () => {
      if (!current()) return;
      // Unlike the pre-ADR-0041 client, CONNECTING is *not* left to the
      // browser: its retry would replay this URL, and this URL's ticket is
      // spent. Close now and mint a new one.
      reopen();
    };

    /*
     * EVERY frame is named, so every name is subscribed. `onmessage` alone
     * receives nothing at all — see events.ts — and a name missing from this
     * loop is a whole class of change that silently never arrives.
     */
    for (const name of STREAM_EVENT_NAMES) {
      es.addEventListener(name, (raw: Event) => {
        if (!current()) return;
        const frame = raw as MessageEvent<string>;
        noteFrame();
        // Recorded before parsing: the resume point is the *transport's*
        // cursor, so a frame this build cannot parse must still advance it or
        // a reopen would replay it forever.
        if (frame.lastEventId !== undefined && frame.lastEventId !== '') {
          after = frame.lastEventId;
        }
        if (!isStreamEventKind(name)) return;
        const event = parseStreamEvent(name as StreamEventKind, frame.data ?? '');
        if (event === undefined) return;
        if (event.kind === 'goodbye') {
          // Orderly, not an error — a restart or a revocation, and the mint
          // tells them apart honestly instead of parsing the human-readable
          // reason: a restarting server mints, a dead session answers 401.
          reopen();
          return;
        }
        options.onEvent(event);
      });
    }
  }

  return {
    start(): void {
      if (!stopped && source !== undefined) return;
      stopped = false;
      attempt = 0;
      void connect();
    },
    stop(): void {
      stop();
    },
    status(): StreamStatus {
      return status;
    },
    lastEventId(): string | undefined {
      return after;
    },
    wake(): void {
      if (stopped) return;
      // An attempt is already in flight, waiting on its mint. Returning to a
      // tab fires `focus` and `visibilitychange` together, and every extra
      // wake here would abandon a ticket the server has already issued.
      if (status === 'connecting') return;
      if (source === undefined || status !== 'open') {
        // Sitting on a backoff (possibly a throttled one). The user is here
        // now; the wait is over.
        if (timer !== undefined) clearTimer(timer);
        timer = undefined;
        attempt = 0;
        void connect();
        return;
      }
      if (silent()) {
        attempt = 0;
        reopen();
      }
    },
    healthy(): boolean {
      return status === 'open' && source !== undefined && !silent();
    },
  };
}
